import {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import {
  IAnsweredItem,
  IFeatureFlag,
  IMergedAnsweredItem,
  IQuotationAnswer,
  IRenegotiateQuotationAnswer,
} from '@/types';
import { calculateTotalItemWithoutDiscount } from '@/utils';
import { handleErrorAsync } from '@/utils/handlers';
import {
  adaptAnswerToRenegotiate,
  createPayload,
  renegotiationHasAnyDifference,
} from '@/utils/quotationRenegotiation';

const RENEGOTIATION_FEATURE_FLAG_CODE =
  'quotation_answer_renegotiation_feature';

interface IQuotationAnswerRenegotiationContext {
  initialAnswer: MutableRefObject<IQuotationAnswer>;
  isAbleToRenegotiate: boolean;
  renegotiationEnabled: boolean;
  setRenegotiationEnabled: Dispatch<SetStateAction<boolean>>;
  answerRenegotiation?: IRenegotiateQuotationAnswer;
  setAnswerRenegotiation: Dispatch<
    SetStateAction<IRenegotiateQuotationAnswer | undefined>
  >;
}

const QuotationAnswerRenegotiationContext = createContext(
  {} as IQuotationAnswerRenegotiationContext
);

QuotationAnswerRenegotiationContext.displayName =
  'QuotationAnswerRenegotiationContext';

interface QuotationAnswerRenegotiationProviderProps {
  children: ReactNode;
}

export const QuotationAnswerRenegotiationProvider = ({
  children,
}: QuotationAnswerRenegotiationProviderProps) => {
  const initialAnswer = useRef({} as IQuotationAnswer);
  const [renegotiationEnabled, setRenegotiationEnabled] = useState(false);
  const [answerRenegotiation, setAnswerRenegotiation] = useState<
    IRenegotiateQuotationAnswer | undefined
  >();
  const [featureFlagIsActive, setFeatureFlagIsActive] = useState(false);

  const quotationAnswerHasOrders = initialAnswer.current.orders?.length > 0;

  const isAbleToRenegotiate =
    featureFlagIsActive && !isExpiredQuotation() && !quotationAnswerHasOrders;

  function isExpiredQuotation() {
    const quotation = initialAnswer.current.quotation;

    if (!quotation) return false;

    const expirationTime = new Date(quotation.expires_at).getTime();
    const currentTime = new Date().getTime();

    return expirationTime <= currentTime;
  }

  useEffect(() => {
    api
      .get<IFeatureFlag>(
        `/api/feature_flags/${RENEGOTIATION_FEATURE_FLAG_CODE}/`
      )
      .then(response => setFeatureFlagIsActive(response.data.is_active))
      .catch(() => setFeatureFlagIsActive(false));
  }, []);

  return (
    <QuotationAnswerRenegotiationContext.Provider
      value={{
        initialAnswer,
        isAbleToRenegotiate,
        renegotiationEnabled,
        setRenegotiationEnabled,
        answerRenegotiation,
        setAnswerRenegotiation,
      }}
    >
      {children}
    </QuotationAnswerRenegotiationContext.Provider>
  );
};

export const useRenegotiation = (quotationAnswer?: IQuotationAnswer) => {
  const context = useContext(QuotationAnswerRenegotiationContext);

  if (!context) {
    throw Error(
      'useRenegotiation hook must be used inside a QuotationAnswerRenegotiationProvider'
    );
  }

  const enableRenegotiation = () => {
    if (!context.initialAnswer.current)
      throw Error('An initial quotation answer must be provided for context');

    if (!context.isAbleToRenegotiate) return;

    context.setRenegotiationEnabled(true);
  };
  const disableRenegotiation = () => context.setRenegotiationEnabled(false);

  const updateMergedItemPrice = useCallback(
    (key: string, valueMicros: number) => {
      const getCurrentTotalDiscount = (mergedItems: IMergedAnsweredItem[]) =>
        mergedItems.reduce(
          (acc, { answered_items }) =>
            acc +
            answered_items.reduce(
              (acc, { total_discount_micros }) => acc + total_discount_micros,
              0
            ),
          0
        ) || 0;

      context.setAnswerRenegotiation(prevRenegotiation => {
        if (!prevRenegotiation) return;

        const currentTotalDiscount = getCurrentTotalDiscount(
          prevRenegotiation.merged_answered_items
        );

        const hasDiscount = currentTotalDiscount !== 0;

        if (hasDiscount) {
          Toast('warning', 'Você terá que re-definir o desconto total.');
        }

        return {
          ...prevRenegotiation,
          merged_answered_items: prevRenegotiation.merged_answered_items.map(
            previousMergedItem => {
              if (previousMergedItem.key !== key) {
                if (!hasDiscount) return previousMergedItem;

                return {
                  ...previousMergedItem,
                  answered_items: previousMergedItem.answered_items.map(
                    answeredItem => ({
                      ...answeredItem,
                      total_discount_micros: 0,
                    })
                  ),
                };
              }

              return {
                ...previousMergedItem,
                answered_items: previousMergedItem.answered_items.map(
                  answeredItem => {
                    const originalQuantity =
                      context.initialAnswer.current.merged_answered_items
                        .find(oldMergedItem => oldMergedItem.key === key)
                        ?.answered_items.find(
                          ({ id }) => id === answeredItem.id
                        )?.total_quantity_micros;

                    return {
                      ...answeredItem,
                      total_discount_micros: !hasDiscount
                        ? answeredItem.total_discount_micros
                        : 0,
                      unit_price_micros: valueMicros,
                      available: Boolean(valueMicros),
                      total_quantity_micros: valueMicros
                        ? originalQuantity ||
                          answeredItem.item.total_quantity_micros
                        : 0,
                      answered: true,
                    };
                  }
                ),
              };
            }
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateShippingAddressFreight = (id: number, freightMicros: number) => {
    context.setAnswerRenegotiation(prevRenegotiation => {
      if (!prevRenegotiation) return;

      return {
        ...prevRenegotiation,
        answered_shipping_addresses:
          prevRenegotiation.answered_shipping_addresses.map(shippingAddress => {
            if (shippingAddress.id !== id) return shippingAddress;

            return {
              ...shippingAddress,
              free_shipping: !freightMicros,
              total_freight_micros: freightMicros,
              answered: true,
            };
          }),
      };
    });
  };

  const updateAnswerRenegotiation = (
    renegotiationUpdated: Partial<IRenegotiateQuotationAnswer>
  ) => {
    context.setAnswerRenegotiation(prev => {
      if (!prev) return;
      return { ...prev, ...renegotiationUpdated };
    });
  };

  const updateDiscount = (discountMicros: number) => {
    if (!discountMicros) {
      context.setAnswerRenegotiation(prev => {
        if (!prev) return;
        return {
          ...prev,
          merged_answered_items: prev.merged_answered_items.map(mergedItem => ({
            ...mergedItem,
            answered_items: mergedItem.answered_items.map(answeredItem => ({
              ...answeredItem,
              total_discount_micros: 0,
            })),
          })),
        };
      });
      return;
    }

    const answeredItems =
      context.answerRenegotiation!.merged_answered_items.flatMap(
        ({ answered_items }) => answered_items
      );

    if (
      answeredItems &&
      renegotiationTotalItemsAmount - discountMicros <
        answeredItems.filter(i => i.available).length * 10000
    ) {
      Toast(
        'error',
        'Desconto não pode ser maior do que o valor dos itens disponíveis'
      );
      return;
    }

    let sumBaseDiscount = 0;
    const newAnsweredItemsWithBaseDiscount = answeredItems.map(
      (item: IAnsweredItem) => {
        if (!item.available) {
          return { ...item, discount: undefined, total_discount_micros: 0 };
        }

        const baseDiscount =
          Math.floor(
            (discountMicros * calculateTotalItemWithoutDiscount(item)) /
              renegotiationTotalItemsAmount /
              10000
          ) * 10000 || 0;

        sumBaseDiscount += baseDiscount;

        return {
          ...item,
          discount: undefined,
          total_discount_micros: baseDiscount,
        };
      }
    );

    const totalRemainder = discountMicros - sumBaseDiscount;
    let availableItemCount = answeredItems.filter(i => i.available).length;
    let sumRemainder = 0;

    const newAnsweredItems = newAnsweredItemsWithBaseDiscount
      .sort(
        (a, b) =>
          calculateTotalItemWithoutDiscount(a) -
          calculateTotalItemWithoutDiscount(b)
      )
      .map((item: IAnsweredItem) => {
        if (!item.available) return item;

        const isLastAvailableItem = availableItemCount === 1;
        const remainder = isLastAvailableItem
          ? totalRemainder - sumRemainder
          : Math.floor(
              (totalRemainder *
                (calculateTotalItemWithoutDiscount(item) /
                  renegotiationTotalItemsAmount)) /
                10000
            ) * 10000;

        sumRemainder += remainder;
        availableItemCount -= 1;

        return {
          ...item,
          discount: undefined,
          total_discount_micros: item.total_discount_micros + remainder,
        };
      });

    context.setAnswerRenegotiation(prev => {
      if (!prev) return;
      return {
        ...prev,
        merged_answered_items: prev.merged_answered_items.map(mergedItem => ({
          ...mergedItem,
          answered_items: mergedItem.answered_items.map(answeredItem => {
            return newAnsweredItems.find(({ id }) => id === answeredItem.id)!;
          }),
        })),
      };
    });
  };

  function getItemSubTotal(mergedItemKey: string) {
    const currentMergedItem =
      context.answerRenegotiation?.merged_answered_items.find(
        ({ key }) => key === mergedItemKey
      );

    const answeredItem = currentMergedItem!.answered_items[0];

    const totalQuantityMicros = currentMergedItem!.answered_items.reduce(
      (acc, item) => {
        if (!item.available) return acc;
        return acc + item.total_quantity_micros;
      },
      0
    );

    const subtotal =
      (totalQuantityMicros * answeredItem.unit_price_micros || 0) / 1000000;

    return subtotal;
  }

  const renegotiationTotalItemsAmount =
    context.answerRenegotiation?.merged_answered_items.reduce(
      (acc, { answered_items }) =>
        acc +
        answered_items.reduce(
          (acc, { total_quantity_micros, unit_price_micros }) =>
            acc + (total_quantity_micros * unit_price_micros) / 1000000,
          0
        ),
      0
    ) || 0;

  const renegotiationTotalDiscount =
    context.answerRenegotiation?.merged_answered_items.reduce(
      (acc, mergedItem) =>
        acc +
        mergedItem.answered_items.reduce(
          (acc, { total_discount_micros }) => acc + total_discount_micros,
          0
        ),
      0
    ) || 0;

  const renegotiationTotalFreight =
    context.answerRenegotiation?.answered_shipping_addresses.reduce(
      (acc, { total_freight_micros }) => acc + total_freight_micros,
      0
    ) || 0;

  const renegotiationTotalAmount =
    (context.answerRenegotiation?.merged_answered_items.reduce(
      (acc, { key }) => acc + getItemSubTotal(key),
      0
    ) || 0) + renegotiationTotalFreight;

  function getMergedItem(mergedItemKey: string) {
    return context.answerRenegotiation?.merged_answered_items.find(
      ({ key }) => key === mergedItemKey
    );
  }

  const sendRenegotiation = async () => {
    const fallbackErrorMessage = 'Ocorreu um erro ao enviar a nova proposta';

    if (!context.answerRenegotiation) throw Error(fallbackErrorMessage);

    const hasDifference = renegotiationHasAnyDifference(
      context.answerRenegotiation,
      context.initialAnswer.current
    );

    if (!hasDifference) {
      throw Error('A nova proposta deve ser diferente da proposta atual!');
    }

    if (!context.answerRenegotiation.valid_until) {
      throw Error('Você deve preencher uma data de validade para a proposta');
    }

    if (!context.answerRenegotiation.arrival_estimate) {
      throw Error('Você deve preencher uma previsão de entrega válida');
    }

    const currentDate = new Date();

    const isProposalExpired =
      new Date(context.answerRenegotiation.valid_until).getTime() <=
      currentDate.getTime();

    if (isProposalExpired) {
      throw Error(
        'Certifique-se de que a validade da proposta seja uma data futura'
      );
    }

    const arrivalEstimate = new Date(
      context.answerRenegotiation.arrival_estimate
    );

    arrivalEstimate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const isArrivalEstimateInPast =
      arrivalEstimate.getTime() < currentDate.getTime();

    if (isArrivalEstimateInPast) {
      throw Error(
        'Certifique-se de que a previsão de entrega seja uma data futura'
      );
    }

    if ((context.answerRenegotiation.observations?.length || 0) >= 256) {
      throw Error(
        'Número máximo de 255 caracteres excedido no campo de observações!'
      );
    }

    const payload = createPayload(context.answerRenegotiation);

    const [newAnswer, error] = await handleErrorAsync(async () => {
      const response = await api.post<IQuotationAnswer>(
        '/api/quotation_answers/renegotiate/',
        {
          ...payload,
          previous_answer: context.initialAnswer.current.id,
        }
      );
      return response.data;
    });

    if (error) throw Error(error.message);

    return newAnswer;
  };

  useEffect(() => {
    if (!quotationAnswer) return;

    context.initialAnswer.current = quotationAnswer;
    context.setAnswerRenegotiation(adaptAnswerToRenegotiate(quotationAnswer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationAnswer]);

  return {
    renegotiationEnabled: context.renegotiationEnabled,
    answerRenegotiation: context.answerRenegotiation,
    isAbleToRenegotiate: context.isAbleToRenegotiate,
    renegotiationTotalDiscount,
    renegotiationTotalFreight,
    updateShippingAddressFreight,
    renegotiationTotalAmount,
    renegotiationTotalItemsAmount,
    updateAnswerRenegotiation,
    updateMergedItemPrice,
    disableRenegotiation,
    enableRenegotiation,
    getItemSubTotal,
    updateDiscount,
    getMergedItem,
    sendRenegotiation,
  };
};
